/**
 * The behaviour half of a panel that is furniture on a desk and an overlay on a phone.
 *
 * Everything visual about that change is in app.css and stays there. What cannot be
 * expressed in a stylesheet is what an overlay owes the reader: focus has to move into
 * it, come back out to the thing that opened it, Escape has to close it, and the
 * surface underneath has to stop moving. Three panels need all four — the evidence
 * panel, the artifact rail and the topbar's navigation menu — so it is written once
 * here rather than three times badly.
 */

import { useEffect, useState, type RefObject } from "react";

/** The width below which app.css turns `.evidence` and `.rail` into sheets. Kept in
 * step with the `@media` rule there by hand, for the reason already recorded in
 * EvidencePanel: a duplicated number is worse than a shared one, but the alternative
 * is JavaScript measuring a box to re-derive a fact the stylesheet already decided. */
export const SHEET = "(max-width: 700px)";

/** Whether the panels are currently sheets rather than columns.
 *
 * A live subscription rather than a one-shot read, because the answer decides what a
 * control *does*, not merely how it looks: the evidence header's `×` clears the
 * selection in a column and dismisses the sheet on a phone. Reading the media query
 * once at mount would leave that button doing the wrong thing for the rest of the
 * session after a rotation. */
export function useSheetMode(): boolean {
  const [sheet, setSheet] = useState(() => window.matchMedia(SHEET).matches);
  useEffect(() => {
    const query = window.matchMedia(SHEET);
    const sync = () => setSheet(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return sheet;
}

/**
 * Give one panel the behaviour of a modal sheet while it is open on a phone.
 *
 * Returns the same answer `useSheetMode` does, so a caller that needs both — and both
 * callers do — asks once.
 */
export function useSheet(
  panel: RefObject<HTMLElement | null>,
  open: boolean,
  close: () => void,
  /**
   * The control to hand focus back to, when the caller knows it for certain.
   *
   * The evidence panel and the rail do not: they are opened from citation markers and
   * buttons scattered over whichever surface the reader was on, so the best available
   * answer is "whatever had focus", worked out below. The topbar menu has exactly one
   * opener — its own hamburger — and guessing there would be worse than the truth,
   * because on a touch browser the guess resolves to `<body>` and the fallback then
   * sends focus into the workspace rather than back to the button just tapped.
   */
  opener?: RefObject<HTMLElement | null>,
): boolean {
  const sheet = useSheetMode();

  useEffect(() => {
    if (!sheet || !open) return;

    const opened = opener?.current ?? document.activeElement;
    panel.current?.focus();

    // The page behind must not move. `overscroll-behavior` on the sheet stops a flick
    // inside it from chaining outwards, and the backdrop swallows anything aimed past
    // it — but neither covers a keyboard PageDown, so the body is locked as well.
    const restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = restoreOverflow;
      returnFocus(opened);
    };
  }, [sheet, open, close, panel, opener]);

  return sheet;
}

/** Put the reader back where they were.
 *
 * Someone who tapped a citation halfway through an answer must not have to find that
 * sentence again, so the marker itself is the target — and on a desktop browser the
 * marker is exactly what `document.activeElement` was when the sheet opened.
 *
 * It is not on iOS Safari, which does not focus a button on tap; there the sheet opens
 * with `<body>` active and there is nothing to remember. So the fallback asks the DOM
 * the same question the stylesheet already asks: every surface that can cite something
 * marks its current selection with `aria-current`, so the marker that opened this
 * sheet is still findable by the attribute it grew when it was tapped.
 */
function returnFocus(opener: Element | null): void {
  if (opener instanceof HTMLElement && opener.isConnected && opener !== document.body) {
    opener.focus();
    return;
  }
  document.querySelector<HTMLElement>('.workspace [aria-current="true"]')?.focus();
}
